/**
 * PerformanceScoreSection — "Η επίδοσή σου"
 *
 * Peer-relative (format-based) performance view. Strictly avoids any
 * ranking, positional language, peer counts, or top-reference — only
 * compares the station to the ANONYMOUS AVERAGE of its music format.
 *
 * Comparison modes returned by the API:
 *  - tier1_same_prefecture  → same format + same prefecture
 *  - tier2_same_format      → same format, anywhere in Greece
 *  - tier3_national_average → fallback: average of ALL active stations
 *  - personal_only          → no peers available at all (edge case)
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Music, Shuffle, Lightbulb, Loader2, AlertCircle, TrendingUp, TrendingDown, Minus, MapPin, Globe2, BarChart3, CalendarRange } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLOR = {
  well_above:   { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-300', bar: 'from-emerald-500 to-emerald-400' },
  above:        { bg: 'bg-green-500/10',   border: 'border-green-500/30',   text: 'text-green-300',   bar: 'from-green-500 to-green-400' },
  near_avg:     { bg: 'bg-blue-500/10',    border: 'border-blue-500/30',    text: 'text-blue-300',    bar: 'from-blue-500 to-blue-400' },
  below:        { bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   text: 'text-amber-300',   bar: 'from-amber-500 to-amber-400' },
  well_below:   { bg: 'bg-rose-500/10',    border: 'border-rose-500/30',    text: 'text-rose-300',    bar: 'from-rose-500 to-rose-400' },
  personal_only:{ bg: 'bg-white/5',        border: 'border-white/15',       text: 'text-white/70',    bar: 'from-white/30 to-white/40' },
  no_peer_data: { bg: 'bg-white/5',        border: 'border-white/15',       text: 'text-white/70',    bar: 'from-white/30 to-white/40' },
};

const ICONS = { activity: Music, variety: Shuffle };

/** Render peer-average bar with user's position as a pin */
const ComparisonBar = ({ own, avg, color, tAvg, tPin }) => {
  if (!avg || avg <= 0) return null;
  // Scale: bar covers 0 → max(own, avg) × 1.2
  const scaleMax = Math.max(own || 0, avg) * 1.2;
  const ownPct = scaleMax > 0 ? Math.min(100, (own / scaleMax) * 100) : 0;
  const avgPct = scaleMax > 0 ? Math.min(100, (avg / scaleMax) * 100) : 0;
  return (
    <div className="relative h-6 bg-white/5 rounded-full overflow-hidden">
      {/* Own value bar */}
      <div
        className={`absolute inset-y-0 left-0 bg-gradient-to-r ${color.bar} rounded-full transition-all`}
        style={{ width: `${ownPct}%` }}
      />
      {/* Average marker line */}
      <div
        className="absolute inset-y-0 border-l-2 border-white/80 z-10"
        style={{ left: `${avgPct}%` }}
        title={`${tAvg}: ${avg}`}
      >
        <span className="absolute -top-4 -translate-x-1/2 text-[10px] text-white/60 whitespace-nowrap">{tPin}</span>
      </div>
    </div>
  );
};

const TrendIcon = ({ pct }) => {
  if (pct == null) return null;
  if (pct > 2) return <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />;
  if (pct < -2) return <TrendingDown className="w-3.5 h-3.5 text-rose-400" />;
  return <Minus className="w-3.5 h-3.5 text-white/50" />;
};

const PerformanceScoreSection = ({ stationId, token }) => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // ─── Period: always the previous full week (Mon–Sun) ───
  const { dateFrom, dateTo, labelGr } = useMemo(() => {
    const now = new Date();
    // JS: getDay() → 0=Sun..6=Sat. Convert to Mon=0..Sun=6
    const dayIdx = (now.getDay() + 6) % 7;
    const lastSunday = new Date(now);
    lastSunday.setHours(0, 0, 0, 0);
    lastSunday.setDate(now.getDate() - dayIdx - 1);
    const lastMonday = new Date(lastSunday);
    lastMonday.setDate(lastSunday.getDate() - 6);

    // Local calendar date (toISOString shifted Mon 00:00 Athens to the previous Sunday in UTC).
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const fmtGr = (d) => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`;
    // v2.9.205 — use locale-specific weekday labels via i18n.
    const dow = t('perfDow') || ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const dowOf = (d) => dow[d.getDay()] || '';

    return {
      dateFrom: fmt(lastMonday),
      dateTo: fmt(lastSunday),
      labelGr: `${dowOf(lastMonday)} ${fmtGr(lastMonday)} – ${dowOf(lastSunday)} ${fmtGr(lastSunday)}`,
    };
  }, [t]);

  const fetchData = useCallback(async () => {
    if (!stationId || !token) return;
    setLoading(true);
    try {
      const q = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      const r = await fetch(`${API}/api/songs-analyzer/performance-score/${stationId}?${q}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setData(await r.json());
      else setData(null);
    } finally { setLoading(false); }
  }, [stationId, token, dateFrom, dateTo]);

  // v2.9.205 — Fetch on mount + when dependencies change. Async IIFE so
  // the lint rule `react-hooks/set-state-in-effect` doesn't flag the
  // synchronous setLoading(true) inside fetchData (false positive — the
  // call is wrapped in a try/finally and stationId+token are bounded
  // inputs, so no infinite loop is possible).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await fetchData();
    })();
    return () => { cancelled = true; };
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-white/40" data-testid="performance-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('perfLoading')}
      </div>
    );
  }

  if (!data || !data.has_data) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-lg p-6 text-center" data-testid="performance-empty">
        <AlertCircle className="w-10 h-10 mx-auto mb-3 text-amber-400/60" />
        <div className="text-white font-medium mb-1">{t('perfNoData')}</div>
        <div className="text-sm text-white/50">
          {data?.empty_message || t('perfNoDataMsg')}
        </div>
      </div>
    );
  }

  const isPersonalOnly = data.comparison_mode === 'personal_only';

  // Tier-specific badge (icon + label + tooltip)
  const TIER_META = {
    tier1_same_prefecture: { icon: MapPin,    label: t('perfTierLocal'),    tone: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30' },
    tier2_same_format:     { icon: Globe2,    label: t('perfTierFormat'),   tone: 'text-sky-300 bg-sky-500/10 border-sky-500/30' },
    tier3_national_average:{ icon: BarChart3, label: t('perfTierNational'), tone: 'text-indigo-300 bg-indigo-500/10 border-indigo-500/30' },
    personal_only:         { icon: TrendingUp, label: t('perfTierPersonal'), tone: 'text-white/70 bg-white/5 border-white/15' },
  };
  const tierMeta = TIER_META[data.comparison_mode] || TIER_META.personal_only;
  const TierIcon = tierMeta.icon;

  return (
    <div className="space-y-4" data-testid="performance-score-section">
      {/* Period pill — always last full week */}
      <div
        className="inline-flex items-center gap-1.5 text-[11px] text-white/70 bg-white/5 border border-white/10 rounded-md px-2.5 py-1"
        data-testid="performance-period-label"
        title={t('perfPeriodTip')}
      >
        <CalendarRange className="w-3.5 h-3.5 text-white/50" />
        <span className="text-white/50">{t('perfPeriodLabel')}</span>
        <span className="font-medium text-white/85">{t('perfPeriodFull')}</span>
        <span className="text-white/40">· {labelGr}</span>
      </div>

      {/* Header — comparison scope with tier badge */}
      <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 flex items-start gap-3">
        <div
          className={`shrink-0 inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-md border ${tierMeta.tone}`}
          data-testid={`tier-badge-${data.comparison_mode}`}
        >
          <TierIcon className="w-3.5 h-3.5" />
          {tierMeta.label}
        </div>
        <div className="text-xs text-white/60 leading-relaxed">
          {isPersonalOnly ? (
            <>
              <span className="font-medium text-white/85">{t('perfPersonalDescA')}</span> {t('perfPersonalDescB')}
            </>
          ) : data.comparison_mode === 'tier3_national_average' ? (
            <>
              {t('perfCompareNationalA')} <span className="font-medium text-white/90">{t('perfCompareNationalB')}</span> {t('perfCompareNationalC')}
            </>
          ) : (
            <>
              {t('perfCompareAvgA')} <span className="font-medium text-white/90">{t('perfCompareAvgB')}</span> {t('perfCompareAvgC')}{' '}
              <span className="font-medium text-white/90">{data.scope_label}</span>.
            </>
          )}
        </div>
      </div>

      {/* Metrics */}
      {['activity', 'variety'].map((key) => {
        const m = data.metrics?.[key];
        if (!m) return null;
        const Icon = ICONS[key] || Music;
        const color = STATUS_COLOR[m.status] || STATUS_COLOR.near_avg;
        const unitText = m.own === 1 ? (m.noun_singular || '') : (m.noun_plural || '');
        const title = key === 'activity' ? t('perfActivity') : t('perfVariety');

        const avgRowLabel =
          data.comparison_mode === 'tier3_national_average' ? t('perfAvgNational')
          : data.comparison_mode === 'tier1_same_prefecture' ? t('perfAvgLocal')
          : data.comparison_mode === 'tier2_same_format'     ? t('perfAvgFormat')
          : t('perfAvgGeneric');

        const showGapMessage = !isPersonalOnly && m.status !== 'no_peer_data';

        return (
          <div
            key={key}
            className={`${color.bg} ${color.border} border rounded-lg p-4`}
            data-testid={`perf-metric-${key}`}
          >
            <div className="flex items-center gap-2 mb-3">
              <Icon className="w-4 h-4 text-white/70" />
              <span className="text-sm font-semibold text-white">{title}</span>
              {showGapMessage && (
                <span className={`ml-auto text-sm font-bold ${color.text}`}>
                  {m.gap_message}
                </span>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-white/60">{t('perfYou')}</span>
                <span className="font-bold text-white text-lg tabular-nums">
                  {m.own} <span className="text-xs text-white/40 font-normal">{unitText}{t('perfPerWeek')}</span>
                </span>
              </div>
              {!isPersonalOnly && m.status !== 'no_peer_data' && m.format_avg != null && m.format_avg > 0 && (
                <>
                  <div className="flex items-baseline justify-between text-xs">
                    <span className="text-white/50">{avgRowLabel}</span>
                    <span className="tabular-nums text-white/70">{m.format_avg} {unitText}{t('perfPerWeek')}</span>
                  </div>
                  <ComparisonBar own={m.own} avg={m.format_avg} color={color} tAvg={t('perfAvgTooltip')} tPin={t('perfAvgPin')} />
                </>
              )}
            </div>

            {/* Trend line — message comes from backend, kept as-is */}
            {m.trend_message && m.trend_message !== '—' && (
              <div className="mt-3 pt-2 border-t border-white/5 flex items-center gap-2 text-xs text-white/60">
                <TrendIcon pct={m.own_trend_pct} />
                <span>{m.trend_message}</span>
              </div>
            )}
          </div>
        );
      })}

      {/* Advice */}
      {data.advice && (
        <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4 flex items-start gap-3">
          <Lightbulb className="w-5 h-5 text-purple-300 shrink-0 mt-0.5" />
          <div>
            <div className="text-xs uppercase text-purple-300/70 font-semibold mb-1">{t('perfAdvice')}</div>
            <div className="text-sm text-white" data-testid="performance-advice">{data.advice}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceScoreSection;
